import { datadogRum } from '@datadog/browser-rum';
import { ddGlobalSessionId } from 'utils/telemetry';
import { sanitizePathForDatadog, sanitizeUrlForDatadog } from './sanitizeViewPath';

const REQUIRED_ENVS = [process.env.DATADOG_ID, process.env.DATADOG_TOKEN, process.env.DATADOG_ENV];

const hasAllDatadogEnvs = () => REQUIRED_ENVS.every((env) => !!env);

// RUM attaches view context to every event type, so this cannot be gated on `type === 'view'`.
const sanitizeViewEvent = (event) => {
  if (!event.view) {
    return;
  }

  if (event.view.url) {
    event.view.url = sanitizeUrlForDatadog(event.view.url);
  }

  if (event.view.referrer) {
    event.view.referrer = sanitizeUrlForDatadog(event.view.referrer);
  }
};

// Covers the `/_next/data/<buildId>/<route>.json?<query>` fetch a route change kicks off,
// and the same URL under `error.resource` when that request fails.
const sanitizeResourceUrls = (event) => {
  if (event.resource?.url) {
    event.resource.url = sanitizeUrlForDatadog(event.resource.url);
  }

  if (event.error?.resource?.url) {
    event.error.resource.url = sanitizeUrlForDatadog(event.error.resource.url);
  }
};

const sanitizeEvent = (event) => {
  sanitizeViewEvent(event);
  sanitizeResourceUrls(event);
};

const ddStartView = (router) => {
  const regex = new RegExp(/admin\/compass/i);

  // Track non-Compass views only; redact UUID path segments and PII query params
  if (router?.asPath && !regex.test(router.asPath)) {
    datadogRum.startView({ name: sanitizePathForDatadog(router.asPath) });
  }
};

export const initializeAndStartDD = (router) => {
  if (hasAllDatadogEnvs()) {
    datadogRum.init({
      beforeSend: (event) => {
        sanitizeEvent(event);
      },
      allowedTracingUrls: [
        /https:\/\/.*\.springhealth\.com/,
        /https:\/\/.*\.springhealth\.cloud/,
        /https:\/\/.*\.springtest\.us/,
        /https:\/\/.*\.springhealth\.us/,
        /https:\/\/.*\.on-aptible.\.com/,
      ],
      applicationId: process.env.DATADOG_ID,
      clientToken: process.env.DATADOG_TOKEN,
      defaultPrivacyLevel: 'mask',
      env: process.env.DATADOG_ENV,
      sessionReplaySampleRate: 0,
      sessionSampleRate: 100,
      service: 'admin-portal',
      silentMultipleInit: true,
      site: 'datadoghq.com',
      traceSampleRate: 100,
      trackLongTasks: true,
      trackResources: true,
      trackViewsManually: true,
      version: process.env.DATADOG_RUM_VERSION,
    });

    datadogRum.setGlobalContextProperty('sh_magic_id', ddGlobalSessionId.getSessionId());

    ddStartView(router);
  }
};

export const datadogIdentify = (userIdentifiers) => {
  const { id, scopes } = userIdentifiers?.user || {};

  if (id) {
    datadogRum.setUser({ id, scopes });
  }
};
